import React, { useState } from 'react';
import * as styles from '../styles/components/modal.css.ts';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';

interface ExcludeRowModalProps {
  isOpen: boolean;
  rowCount: number; // 除外する行数
  rowData?: Array<{ rowNumber: number; values: Record<string, unknown> }>; // 行のデータ
  onConfirm: (comment: string) => void;
  onCancel: () => void;
}

export const ExcludeRowModal: React.FC<ExcludeRowModalProps> = ({
  isOpen,
  rowCount,
  rowData,
  onConfirm,
  onCancel
}) => {
  const [comment, setComment] = useState('');

  const handleConfirm = () => {
    onConfirm(comment);
    setComment('');
  };

  const handleCancel = () => {
    onCancel();
    setComment('');
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title={`Exclude ${rowCount === 1 ? 'Row' : `${rowCount} Rows`}`}
      width="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm}>
            Exclude {rowCount === 1 ? 'Row' : 'Rows'}
          </Button>
        </>
      }
    >
      <div>
        <p className={styles.warningText}>
          You are about to exclude {rowCount === 1 ? 'this row' : `${rowCount} rows`} from analysis.
          This action can be undone from the "Excluded Rows" tab.
        </p>

        {/* 除外対象の行情報を表示 */}
        {rowData && rowData.length > 0 && (
          <div className={styles.rowPreview}>
            <div className={styles.previewTitle}>
              {rowCount === 1 ? 'Data to be excluded:' : `Data to be excluded (${rowCount} rows):`}
            </div>
            <div className={styles.previewContent}>
              {rowData.map((row, index) => {
                return (
                  <div key={index} className={styles.previewRow}>
                    {Object.entries(row.values).map(([key, value], i) => {
                      // 値を文字列に変換（null/undefinedの場合は空文字列）
                      const displayValue = value == null ? '' : String(value);
                      // 長すぎる値は省略
                      const truncatedValue = displayValue.length > 50
                        ? displayValue.substring(0, 50) + '...'
                        : displayValue;

                      return (
                        <div key={i} className={styles.columnValue}>
                          <span className={styles.columnName}>{key}:</span>
                          <span className={styles.columnData}>{truncatedValue || '(empty)'}</span>
                        </div>
                      );
                    })}
                    {Object.keys(row.values).length === 0 && (
                      <span className={styles.rowValues}>(no data)</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <label className={styles.label}>
          Reason for exclusion (optional):
        </label>
        <textarea
          className={styles.commentTextarea}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g., Outlier, Data entry error, Test data..."
          rows={3}
          autoFocus
        />
      </div>
    </Dialog>
  );
};